import {modelUniformValues, modelDepthUniformValues} from './program/model_program';
import {ModelTraits, DefaultModelScale, calculateModelMatrix} from '../data/model';
import EXTENT from '../../src/style-spec/data/extent';
import StencilMode from '../../src/gl/stencil_mode';
import ColorMode from '../../src/gl/color_mode';
import DepthMode from '../../src/gl/depth_mode';
import CullFaceMode from '../../src/gl/cull_face_mode';
import {mat4, quat, vec3, vec4} from 'gl-matrix';
import {getMetersPerPixelAtLatitude, mercatorZfromAltitude, tileToMeter} from '../../src/geo/mercator_coordinate';
import TextureSlots from './texture_slots';
import {convertModelMatrixForGlobe} from '../util/model_util';
import {clamp, warnOnce, esgtsaHash} from '../../src/util/util';
import assert from '../../src/style-spec/util/assert';
import {DEMSampler} from '../../src/terrain/elevation';
import {Aabb} from '../../src/util/primitives';
import {getCutoffParams} from '../../src/render/cutoff';
import {FOG_OPACITY_THRESHOLD} from '../../src/style/fog_helpers';
import {ZoomDependentExpression, type Feature, type FeatureState} from '../../src/style-spec/expression/index';
import {Texture3D} from '../../src/render/texture';
import {pointInFootprint} from '../../3d-style/source/replacement_source';
import Point from '@mapbox/point-geometry';
import LngLat from '../../src/geo/lng_lat';
import {tileToLngLat} from '../style/style_layer/model_layer_query';
import SegmentVector from '../../src/data/segment';
import {PosArray, TriangleIndexArray} from '../../src/data/array_types';
import posAttributes from '../../src/data/pos_attributes';
import {debugUniformValues} from '../../src/render/program/debug_program';
import Color from '../../src/style-spec/util/color';
import {lerp} from '../../src/style-spec/util/lerp';

import type EvaluationParameters from '../../src/style/evaluation_parameters';
import type Program from '../../src/render/program';
import type Transform from '../../src/geo/transform';
import type ModelBucket from '../data/bucket/model_bucket';
import type {UniformValues} from '../../src/render/uniform_binding';
import type {OverscaledTileID} from '../../src/source/tile_id';
import type {Tiled3dModelFeature} from '../data/bucket/tiled_3d_model_bucket';
import type Tiled3dModelBucket from '../data/bucket/tiled_3d_model_bucket';
import type Painter from '../../src/render/painter';
import type {CreateProgramParams} from '../../src/render/painter';
import type SourceCache from '../../src/source/source_cache';
import type ModelStyleLayer from '../style/style_layer/model_style_layer';
import type {MaterialOverride, Mesh, ModelMaterialOverrides, ModelNode, ModelNodeOverrides, ModelTexture, NodeOverride} from '../data/model';
import type {DynamicDefinesType} from '../../src/render/program/program_uniforms';
import type VertexBuffer from '../../src/gl/vertex_buffer';
import type {CutoffParams} from '../../src/render/cutoff';
import type {LUT} from "../../src/util/lut";
import type {ModelUniformsType, ModelDepthUniformsType} from '../render/program/model_program';
import type {LightOverrides} from './lights';
import type {ModelPartStyleUBO} from '../data/bucket/model_part_style_ubo';

export default drawModels;

// Module-level scratch buffers reused across hot paths to avoid per-frame allocations.
// Kept as Float64Array because mat4.invert + mat4.transpose on the normal matrix is sensitive
// to precision; Float32 narrowing here caused visible PBR shading diffs (ANGLE/D3D on Windows).
// UniformMatrix4f.set performs the Float32 conversion at upload time.
const lightingMatrixScratch = new Float64Array(16);
const normalMatrixScratch = new Float64Array(16);
const fogMatrixScratch = new Float64Array(16);
// Per-tile caches used in drawBatchedModels: hold (M * tileMatrix) for the current tile,
// then per-node we apply translate/scale on top instead of doing a full mat4.multiply.
const lightingTileMatrix = new Float64Array(16);
const projTileMatrix = new Float64Array(16);
// transpose(invert(lightingTileMatrix)). Per node the normal matrix derives from this with a
// single column scale: translate(t) drops out (upper-3x3 of column 3 is zero for affine M^-T)
// and the per-node S(s) inverts to a column scale by 1/s. Avoids a per-node mat4.invert.
const lightingTileNormalMatrix = new Float64Array(16);
// Per-mesh scratch holding the light-mesh worldViewProjection, rebuilt from projTileMatrix
// before each program.draw. Can't share with the lighting/normal/fog scratches — those are
// all alive at modelUniformValues time.
const wvpTileScratch = new Float64Array(16);
// Per-node column scale applied to lightingTileNormalMatrix: 1/s with a Y-flip baked in.
const normalScaleScratch: vec3 = [0, 0, 0];
// Placeholder passed to modelUniformValues for u_normal_matrix in the instanced path;
// it's either overwritten per instance or unused (shader reads from instance attributes).
const instancedNormalMatrixPlaceholder = new Float32Array(16);
const lodNodeCenterScratch: vec3 = [0, 0, 0];
// Passed as cameraPos to modelUniformValues for batched models (camera origin in tile-space
// is implicitly at zero because tileMatrix already encodes camera translation).
const zeroCameraPos: [number, number, number] = [0, 0, 0];
// Per-node per-cascade shadow light matrices, derived per node by layering translate+scale
// on top of the per-tile cascade*tile cache. Lazily grown to the active cascade count.
const shadowLightMatrices: Float64Array[] = [];

type ModelParameters = {
    zScaleMatrix: mat4;
    negCameraPosMatrix: mat4;
};

type SortedMesh = {
    mesh: Mesh;
    depth: number;
    modelIndex: number;
    worldViewProjection: mat4;
    nodeModelMatrix: mat4;
    isLightMesh: boolean;
    modelOpacity: number;
    materialOverride?: MaterialOverride;
    modelColor?: [number, number, number, number];
    lightOverrides?: LightOverrides;
    node: ModelNode;
    modelMatrix: mat4;
};

type SortedNode = {
    nodeInfo: Tiled3dModelFeature;
    depth: number;
    opacity: number;
    wvpForNode: mat4;
    nodeModelMatrix: mat4;
    tileModelMatrix: mat4;
    // Per-node tile-space translation; reused post-sort to rebuild the lighting matrix
    // from the per-tile cache without doing a full mat4.multiply.
    tileTranslation: vec3;
};

type RenderData = {
    shadowUniformsInitialized: boolean;
    useSingleShadowCascade: boolean;
    tileMatrix: Float64Array;
    shadowTileMatrix: Float32Array;
    aabb: Aabb;
};

function fogMatrixForModel(out: mat4, modelMatrix: mat4, transform: Transform): mat4 {
    // convert model matrix from the default world size to the one used by the fog
    const scale = transform.cameraWorldSizeForFog / transform.worldSize;
    mat4.scale(out, transform.worldToFogMatrix, [scale, scale, 1]);
    mat4.multiply(out, out, modelMatrix);
    return out;
}

// Bake a per-tile (M * tileMatrix) into out, then apply the per-node anchor translation and
// model scale on top. Replaces a full mat4.multiply(out, M, tileModelMatrix) with translate +
// optional scale; mat4.translate doubles as the copy when out !== base.
function applyTileTransform(out: mat4, base: mat4, translation: vec3, scale: vec3): mat4 {
    mat4.translate(out, base, translation);
    if (!vec3.exactEquals(scale, DefaultModelScale)) {
        mat4.scale(out, out, scale);
    }
    return out;
}

// Local z bounds the emissive strength height gradient resolves against, packed as
// u_model_mesh_params: min and range in xy, then whether the mesh is a LOD mesh, whose vertex color
// alpha carries baked ambient occlusion.
//
// The lights borrow the door geometry's bounds, since they are styled as its part and their own
// bounds cover just the light quads.
//
// Returns a fresh array per call: Uniform4f keeps a reference to the value it was handed as its
// change-detection cache, so a reused scratch array would compare equal to itself and the uniform
// would only ever be uploaded for the first mesh drawn.
function modelMeshParams(mesh: Mesh, node: ModelNode, isLight: boolean, isLodMesh: boolean): [number, number, number, number] {
    const aabb = isLight ? node.lightsStyleAabb : mesh.aabb;
    const zMin = aabb ? aabb.min[2] : Infinity;
    const zMax = aabb ? aabb.max[2] : -Infinity;

    const zExtent = zMax - zMin;
    // A zero range would divide by zero in the shader, and an empty aabb leaves it non-finite.
    return [
        isFinite(zMin) ? zMin : 0,
        zExtent > 0 && isFinite(zExtent) ? zExtent : 1,
        isLodMesh ? 1 : 0,
        0
    ];
}

// The node's part style block, but only for a mesh carrying the part id attribute that indexes it.
// The two are compiled and bound as a pair, so this is the single gate for both.
function partStyleUBOForMesh(nodeInfo: Tiled3dModelFeature, mesh: Mesh): ModelPartStyleUBO | null {
    return mesh.featureBuffer ? nodeInfo.partStyleUBO : null;
}

// Collect defines and dynamic buffers (colors, normals, uv) and bind textures. Used for single mesh and instanced draw.
function setupMeshDraw(definesValues: Array<string>, dynamicBuffers: Array<VertexBuffer | null | undefined>, mesh: Mesh, painter: Painter, lut: LUT | null, partStyleUBO: ModelPartStyleUBO | null = null) {
    const material = mesh.material;
    const context = painter.context;

    const {baseColorTexture, metallicRoughnessTexture} = material.pbrMetallicRoughness;
    const {normalTexture, occlusionTexture, emissionTexture} = material;

    function setupTexture(texture: ModelTexture | null | undefined, define: string, slot: number) {
        if (!texture) return;

        definesValues.push(define);
        context.activeTexture.set(context.gl.TEXTURE0 + slot);
        if (texture.gfxTexture) {
            const {minFilter, magFilter, wrapS, wrapT} = texture.sampler;
            texture.gfxTexture.bindExtraParam(minFilter, magFilter, wrapS, wrapT);
        }
    }

    // Textures
    setupTexture(baseColorTexture, 'HAS_TEXTURE_u_baseColorTexture', TextureSlots.BaseColor);
    setupTexture(metallicRoughnessTexture, 'HAS_TEXTURE_u_metallicRoughnessTexture', TextureSlots.MetallicRoughness);
    setupTexture(normalTexture, 'HAS_TEXTURE_u_normalTexture', TextureSlots.Normal);
    setupTexture(occlusionTexture, 'HAS_TEXTURE_u_occlusionTexture', TextureSlots.Occlusion);
    setupTexture(emissionTexture, 'HAS_TEXTURE_u_emissionTexture', TextureSlots.Emission);

    if (lut) {
        if (!lut.texture) {
            lut.texture = new Texture3D(painter.context, lut.image, [lut.image.height, lut.image.height, lut.image.height], context.gl.RGBA8);
        }
        context.activeTexture.set(context.gl.TEXTURE0 + TextureSlots.LUT);
        if (lut.texture) {
            lut.texture.bind(context.gl.LINEAR, context.gl.CLAMP_TO_EDGE);
        }
        definesValues.push('APPLY_LUT_ON_GPU');
    }

    if (mesh.texcoordBuffer) {
        definesValues.push('HAS_ATTRIBUTE_a_uv_2f');
        dynamicBuffers.push(mesh.texcoordBuffer);
    }
    if (mesh.colorBuffer) {
        const colorDefine = (mesh.colorBuffer.itemSize === 12) ? 'HAS_ATTRIBUTE_a_color_3f' : 'HAS_ATTRIBUTE_a_color_4f';
        definesValues.push(colorDefine);
        dynamicBuffers.push(mesh.colorBuffer);
    }
    if (mesh.normalBuffer) {
        definesValues.push('HAS_ATTRIBUTE_a_normal_4n');
        dynamicBuffers.push(mesh.normalBuffer);
    }

    // The define is what compiles the block into the shader, so callers must pass the UBO only for a
    // mesh that also has the attribute (see partStyleUBOForMesh). Without the block a vertex reads
    // alpha 0 and the mesh disappears; without the attribute every vertex reads part 0 and a black
    // vertex color. Only the batched path has a node to take the UBO from, so a mesh reaching the
    // other callers with feature data would silently lose its part styling.
    assert(partStyleUBO || !mesh.featureBuffer);
    if (partStyleUBO) {
        definesValues.push('HAS_ATTRIBUTE_a_feature');
        dynamicBuffers.push(mesh.featureBuffer);
    }

    if (material.alphaMode === 'OPAQUE' || material.alphaMode === 'MASK') {
        definesValues.push('UNPREMULT_TEXTURE_IN_SHADER');
    }

    // just to make the rendertests the same than native
    if (!material.defined) {
        definesValues.push('DIFFUSE_SHADED');
    }

    const shadowRenderer = painter.shadowRenderer;
    if (shadowRenderer) {
        definesValues.push('RENDER_SHADOWS');
        if (shadowRenderer.useNormalOffset) {
            definesValues.push('NORMAL_OFFSET');
        }
    }
}

function drawMesh(sortedMesh: SortedMesh, painter: Painter, layer: ModelStyleLayer, modelParameters: ModelParameters, stencilMode: StencilMode, colorMode: ColorMode) {
    const opacity = sortedMesh.modelOpacity;

    assert(opacity > 0);
    const context = painter.context;
    const depthMode = new DepthMode(painter.context.gl.LEQUAL, sortedMesh.isLightMesh ? DepthMode.ReadOnly : DepthMode.ReadWrite, painter.depthRangeFor3D);
    const tr = painter.transform;

    const mesh = sortedMesh.mesh;
    const material = mesh.material;
    const pbr = material.pbrMetallicRoughness;
    const fog = painter.style.fog;

    if (painter.transform.projection.zAxisUnit === "pixels") {
        lightingMatrixScratch.set(sortedMesh.nodeModelMatrix);
    } else {
        mat4.multiply(lightingMatrixScratch, modelParameters.zScaleMatrix, sortedMesh.nodeModelMatrix);
    }
    mat4.multiply(lightingMatrixScratch, modelParameters.negCameraPosMatrix, lightingMatrixScratch);
    mat4.invert(normalMatrixScratch, lightingMatrixScratch);
    mat4.transpose(normalMatrixScratch, normalMatrixScratch);

    const ignoreLut = layer.paint.get('model-color-use-theme').constantOr('default') === 'none';
    const emissiveStrength = layer.paint.get('model-emissive-strength').constantOr(0.0);

    const programOptions: CreateProgramParams = {
        defines: []
    };

    // Extra buffers (colors, normals, texCoords)
    const dynamicBuffers: (VertexBuffer | null | undefined)[] | undefined = [];

    const shadowRenderer = painter.shadowRenderer;
    if (shadowRenderer) { shadowRenderer.useNormalOffset = false; }

    setupMeshDraw((programOptions.defines), dynamicBuffers, mesh, painter, ignoreLut ? null : layer.lut);

    let fogMatrix: mat4 | null = null;
    if (fog) {
        fogMatrix = fogMatrixForModel(fogMatrixScratch, sortedMesh.nodeModelMatrix, painter.transform);

        if (tr.projection.name !== 'globe') {
            const min = mesh.aabb.min;
            const max = mesh.aabb.max;
            const [minOpacity, maxOpacity] = fog.getOpacityForBounds(fogMatrix, min[0], min[1], max[0], max[1]);
            programOptions.overrideFog = minOpacity >= FOG_OPACITY_THRESHOLD || maxOpacity >= FOG_OPACITY_THRESHOLD;
        }
    }

    const cutoffParams = getCutoffParams(painter, layer.paint.get('model-cutoff-fade-range'));
    if (cutoffParams.shouldRenderCutoff) {
        programOptions.defines.push('RENDER_CUTOFF');
    }

    const program = painter.getOrCreateProgram('model', programOptions);

    const uniformValues = modelUniformValues(
        sortedMesh.worldViewProjection,
        lightingMatrixScratch,
        normalMatrixScratch,
        null,
        painter,
        opacity,
        pbr.baseColorFactor,
        material.emissiveFactor,
        pbr.metallicFactor,
        pbr.roughnessFactor,
        material,
        emissiveStrength,
        layer,
        undefined,
        undefined,
        sortedMesh.materialOverride,
        sortedMesh.modelColor,
        1.0,
        program.fixedDefines.includes('LIGHTING_3D_MODE'));

    painter.uploadCommonUniforms(context, program, null, fogMatrix, cutoffParams, sortedMesh.lightOverrides);

    const isShadowPass = painter.renderPass === 'shadow';

    if (!isShadowPass && shadowRenderer) {
        shadowRenderer.setupShadowsFromMatrix(sortedMesh.nodeModelMatrix, program);
    }

    const cullFaceMode = mesh.material.doubleSided ? CullFaceMode.disabled : CullFaceMode.backCCW;

    program.draw(painter, context.gl.TRIANGLES, depthMode, stencilMode, colorMode, cullFaceMode,
            uniformValues, layer.id, mesh.vertexBuffer, mesh.indexBuffer, mesh.segments, layer.paint, painter.transform.zoom,

            undefined, dynamicBuffers);
}

function getScope(painter: Painter, layer: ModelStyleLayer) {
    return painter.style._importedAsBasemap ? "basemap" : layer.scope;
}

export function prepare(layer: ModelStyleLayer, sourceCache: SourceCache, painter: Painter) {
    const modelSource = sourceCache.getSource();
    if (!modelSource.loaded()) return;

    if (modelSource.type === 'vector' || modelSource.type === 'geojson') {
        if (painter.modelManager) {
            // Do it here, to prevent modelManager handling in Painter.
            // geojson models are always set in the root scope to avoid model duplication
            painter.modelManager.upload(painter, getScope(painter, layer));
        }
        return;
    }

    if (modelSource.type === 'batched-model') {
        // batched models uploads happen in tile_3d_bucket
        return;
    }

    if (modelSource.type !== 'model') return;

    const models = modelSource.getModels();
    // Upload models
    for (const model of models) {
        model.upload(painter.context);
    }
}

function prepareMeshes(painter: Painter, node: ModelNode, modelMatrix: mat4, projectionMatrix: mat4, modelIndex: number, transparentMeshes: Array<SortedMesh>, opaqueMeshes: Array<SortedMesh>, materialOverrides: ModelMaterialOverrides, modelOpacity: number, modelColorMix?: [number, number, number, number], lightOverrides?: LightOverrides) {

    const transform = painter.transform;

    const isGlobe = transform.projection.name === 'globe';
    const isShadowPass = painter.renderPass === 'shadow';
    const isLightMesh = node.isGeometryBloom ? node.isGeometryBloom : false;

    if (node.minZoom !== undefined) {
        if (painter.transform.zoom < node.minZoom) return;
    }
    if (node.maxZoom !== undefined) {
        if (painter.transform.zoom > node.maxZoom) return;
    }

    // Skip bloom geometry meshes for shadow pass
    if (isLightMesh && isShadowPass) return;

    const nodeModelMatrix = isGlobe ? convertModelMatrixForGlobe(modelMatrix, transform) : [...modelMatrix];

    mat4.multiply(nodeModelMatrix, nodeModelMatrix, node.globalMatrix);
    const worldViewProjection = mat4.multiply([], projectionMatrix, nodeModelMatrix);
    if (node.meshes) {
        for (const mesh of node.meshes) {
            const materialOverride = materialOverrides.get(mesh.material.name);

            if (materialOverride && materialOverride.opacity <= 0) continue;

            if (mesh.material.alphaMode !== 'BLEND') {
                const opaqueMesh: SortedMesh = {mesh, depth: 0.0, modelIndex, worldViewProjection, nodeModelMatrix, isLightMesh, materialOverride, modelOpacity, modelColor: modelColorMix, lightOverrides, node, modelMatrix};
                opaqueMeshes.push(opaqueMesh);
                continue;
            }

            const centroidPos = vec3.transformMat4([], mesh.centroid, worldViewProjection);
            // Filter meshes behind the camera if in perspective mode
            if (!transform.isOrthographic && centroidPos[2] <= 0.0) continue;
            const transparentMesh: SortedMesh = {mesh, depth: centroidPos[2], modelIndex, worldViewProjection, nodeModelMatrix, isLightMesh, materialOverride, modelOpacity, modelColor: modelColorMix, lightOverrides, node, modelMatrix};
            transparentMeshes.push(transparentMesh);
        }
    }
    if (node.children) {
        for (const child of node.children) {
            prepareMeshes(painter, child, modelMatrix, projectionMatrix, modelIndex, transparentMeshes, opaqueMeshes, materialOverrides, modelOpacity, modelColorMix, lightOverrides);
        }
    }
}

function drawShadowCaster(mesh: Mesh, matrix: mat4, painter: Painter, layer: ModelStyleLayer, precomputedShadowMatrix?: mat4) {
    const shadowRenderer = painter.shadowRenderer;
    if (!shadowRenderer) return;
    const depthMode = shadowRenderer.getShadowPassDepthMode();
    const shadowMatrix = precomputedShadowMatrix || shadowRenderer.calculateShadowPassMatrixFromMatrix(matrix);
    const uniformValues = modelDepthUniformValues(shadowMatrix);
    const program = painter.getOrCreateProgram('modelDepth');
    const context = painter.context;
    program.draw(painter, context.gl.TRIANGLES, depthMode, StencilMode.disabled, ColorMode.disabled, CullFaceMode.disabled,
        uniformValues, layer.id, mesh.vertexBuffer, mesh.indexBuffer, mesh.segments, layer.paint, painter.transform.zoom,
        undefined, undefined);
}

function getOrCreateFootprintMesh(painter: Painter, node: ModelNode) {
    if (node.footprintDebugMesh) return node.footprintDebugMesh;
    if (!node.footprint) return null;

    const context = painter.context;
    const vertices = node.footprint.vertices;
    const indices = node.footprint.indices;

    const vertexArray = new PosArray();
    vertexArray.reserve(vertices.length / 2);
    for (let i = 0; i < vertices.length; i += 2) {
        vertexArray.emplaceBack(vertices[i], vertices[i + 1]);
    }

    const indexArray = new TriangleIndexArray();
    indexArray.reserve(indices.length);
    for (let i = 0; i < indices.length; i += 3) {
        indexArray.emplaceBack(indices[i], indices[i + 1], indices[i + 2]);
    }

    const vertexBuffer = context.createVertexBuffer(vertexArray, posAttributes.members);
    const indexBuffer = context.createIndexBuffer(indexArray);
    const segments = SegmentVector.simpleSegment(0, 0, vertices.length, indices.length);

    // Generate a deterministic color based on the node ID or name
    const idStr = node.id || node.name || 'footprint';
    let seed: number;

    const numericId = parseInt(idStr, 10);
    if (!isNaN(numericId)) {
        seed = numericId;
    } else {
        seed = stringHash(idStr);
    }

    const r = esgtsaHash(seed);
    const g = esgtsaHash(seed + 1);
    const b = esgtsaHash(seed + 2);

    node.footprintDebugMesh = {
        vertexBuffer,
        indexBuffer,
        segments,
        color: new Color(r, g, b, 0.5)
    };

    return node.footprintDebugMesh;
}

function stringHash(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = (((hash << 5) - hash) + str.charCodeAt(i)) | 0;
    }
    return hash;
}

function drawFootprint(painter: Painter, layer: ModelStyleLayer, node: ModelNode, mvpMatrix: mat4) {
    const mesh = getOrCreateFootprintMesh(painter, node);
    if (!mesh) return;

    const context = painter.context;
    const gl = context.gl;

    const program = painter.getOrCreateProgram('debug');

    const color = mesh.color;
    const depthMode = DepthMode.disabled;
    // We bind the empty (transparent) texture to ensure no overlay is drawn.
    context.activeTexture.set(gl.TEXTURE0);
    painter.emptyTexture.bind(gl.LINEAR, gl.CLAMP_TO_EDGE);
    program.draw(painter, gl.TRIANGLES, depthMode, StencilMode.disabled, ColorMode.alphaBlended, CullFaceMode.disabled,
        debugUniformValues(mvpMatrix, color.toPremultipliedRenderColor(null)), '$debug',
        mesh.vertexBuffer, mesh.indexBuffer, mesh.segments);
}

// Evaluate feature state for node names
function evaluateFeatureStateForNodeOverrides(layer: ModelStyleLayer, featureId: string | number, featureState: FeatureState, featureProperties: Record<string, unknown>, nodeNamesToEvaluate: string[], nodeOverrides: ModelNodeOverrides) {
    for (const nodeId of nodeNamesToEvaluate) {
        const partProperties = {...featureProperties};
        partProperties['part'] = nodeId;

        const part: Feature = {
            type: 'Unknown',
            id: featureId,
            properties: partProperties
        };

        const nodeOverride: NodeOverride = {
            orientation: layer.paint.get('model-rotation').evaluate(part, featureState),
        };
        nodeOverrides.set(nodeId, nodeOverride);
    }
}

// Evaluate feature state for material names
function evaluateFeatureStateForMaterialOverrides(layer: ModelStyleLayer, featureId: string | number, featureState: FeatureState, featureProperties: Record<string, unknown>, materialNamesToEvaluate: string[], materialOverrides: ModelMaterialOverrides) {
    for (const materialId of materialNamesToEvaluate) {
        const partProperties = {...featureProperties};
        partProperties['part'] = materialId;

        const part: Feature = {
            type: 'Unknown',
            id: featureId,
            properties: partProperties
        };

        const materialOverride: MaterialOverride = {
            color: layer.paint.get('model-color').evaluate(part, featureState),
            colorMix: layer.paint.get('model-color-mix-intensity').evaluate(part, featureState),
            opacity: layer.paint.get('model-opacity').evaluate(part, featureState),
            emissionStrength: layer.paint.get('model-emissive-strength').evaluate(part, featureState),
        };
        materialOverrides.set(materialId, materialOverride);
    }
}

function drawModels(painter: Painter, sourceCache: SourceCache, layer: ModelStyleLayer, coords: Array<OverscaledTileID>) {
    if (painter.renderPass === 'opaque') {
        return;
    }

    // early return if totally transparent
    const opacity = layer.paint.get('model-opacity').constantOr(1.0);

    const elevationReference = layer.paint.get('model-elevation-reference');
    const shouldFollowTerrainSlope = elevationReference === 'ground';
    const shouldApplyElevation = elevationReference === 'ground';

    if (opacity === 0) {
        return;
    }
    const castShadows = layer.paint.get('model-cast-shadows');
    if (painter.renderPass === 'shadow') {
        if (!castShadows) {
            return;
        }
        if (painter.terrain) {
            const noShadowCutoff = 0.65;

            if (opacity < noShadowCutoff) {
                const expression = layer._transitionablePaint._values['model-opacity'].value.expression;
                if (expression instanceof ZoomDependentExpression) {
                    // avoid rendering shadows during fade in / fade out on terrain
                    return;
                }
            }
        }
    }
    const shadowRenderer = painter.shadowRenderer;
    const receiveShadows = layer.paint.get('model-receive-shadows');
    if (shadowRenderer) {
        shadowRenderer.useNormalOffset = true;
        if (!receiveShadows) {
            shadowRenderer.enabled = false;
        }
    }
    const cleanup = () => {
        if (shadowRenderer) {
            shadowRenderer.useNormalOffset = true;
            if (!receiveShadows) {
                shadowRenderer.enabled = true;
            }
        }
    };

    const modelSource = sourceCache.getSource();
    if (painter.renderPass === 'light-beam' && modelSource.type !== 'batched-model') {
        return;
    }

    if (modelSource.type === 'vector' || modelSource.type === 'geojson') {
        drawVectorLayerModels(painter, sourceCache, layer, coords, getScope(painter, layer));
        cleanup();
        return;
    }

    if (!modelSource.loaded()) return;

    if (modelSource.type === 'batched-model') {
        drawBatchedModels(painter, sourceCache, layer, coords);
        cleanup();
        return;
    }

    if (modelSource.type !== 'model') return;

    const models = modelSource.getModels();
    const modelParametersVector: ModelParameters[] = [];

    const mercCameraPos = painter.transform.getFreeCameraOptions().position;
    const cameraPos = vec3.scale([], [mercCameraPos.x, mercCameraPos.y, mercCameraPos.z], painter.transform.worldSize);
    vec3.negate(cameraPos, cameraPos);
    const transparentMeshes: SortedMesh[] = [];
    const opaqueMeshes: SortedMesh[] = [];
    let modelIndex = 0;
    // Draw models
    for (const model of models) {
        // Resolve feature-state based overrides
        const modelFeatureState = sourceCache.getFeatureState('', model.id);

        const modelFeature: Feature = {
            type: 'Unknown',
            id: model.id,
            properties: model.featureProperties
        };
        const rotation = layer.paint.get('model-rotation').evaluate(modelFeature, modelFeatureState);
        const scale = layer.paint.get('model-scale').evaluate(modelFeature, modelFeatureState);
        const translation = layer.paint.get('model-translation').evaluate(modelFeature, modelFeatureState);
        const modelOpacity = layer.paint.get('model-opacity').evaluate(modelFeature, modelFeatureState);

        evaluateFeatureStateForNodeOverrides(layer, model.id, modelFeatureState, model.featureProperties, model.nodeOverrideNames, model.nodeOverrides);
        evaluateFeatureStateForMaterialOverrides(layer, model.id, modelFeatureState, model.featureProperties, model.materialOverrideNames, model.materialOverrides);

        // Update node transform with overrides (if any)
        if (model.nodeOverrides.size > 0) {
            model.computeBoundsAndApplyParent();
        }
        // update model matrices
        model.computeModelMatrix(painter, rotation, scale, translation, shouldApplyElevation, shouldFollowTerrainSlope, false);

        // compute model parameters matrices
        const negCameraPosMatrix = mat4.fromTranslation([], cameraPos);
        const modelMetersPerPixel = getMetersPerPixelAtLatitude(model.position.lat, painter.transform.zoom);
        const modelPixelsPerMeter = 1.0 / modelMetersPerPixel;
        const zScaleMatrix = mat4.fromScaling([], [1.0, 1.0, modelPixelsPerMeter]);
        const modelParameters = {zScaleMatrix, negCameraPosMatrix};
        modelParametersVector.push(modelParameters);
        for (const node of model.nodes) {
            prepareMeshes(painter, node, model.matrix, painter.transform.expandedFarZProjMatrix, modelIndex, transparentMeshes, opaqueMeshes, model.materialOverrides, modelOpacity, undefined, model.lightOverrides);
        }
        modelIndex++;
    }
    // Sort the transparent meshes by depth
    transparentMeshes.sort((a, b) => {
        return b.depth - a.depth;
    });

    if (painter.renderPass === 'shadow') {
        for (const opaqueMesh of opaqueMeshes) {
            drawShadowCaster(opaqueMesh.mesh, opaqueMesh.nodeModelMatrix, painter, layer);
        }
        // Draw transparent sorted meshes
        for (const transparentMesh of transparentMeshes) {
            drawShadowCaster(transparentMesh.mesh, transparentMesh.nodeModelMatrix, painter, layer);
        }
        // Finish the render pass
        cleanup();
        return;
    }

    if (painter._debugParams.show3DModelFootprints) {
        const proj = painter.transform.projMatrix;
        const footprints = new Map<string, {node: ModelNode, mvp: mat4}>();

        const addFootprint = (node: ModelNode, matrix: mat4) => {
            if (node.footprint) {
                const id = node.id || node.name || 'footprint';
                if (!footprints.has(id)) {
                    const mvp = mat4.multiply([], proj, matrix);
                    footprints.set(id, {node, mvp});
                }
            }
        };

        for (const opaqueMesh of opaqueMeshes) {
            addFootprint(opaqueMesh.node, opaqueMesh.modelMatrix);
        }
        for (const transparentMesh of transparentMeshes) {
            addFootprint(transparentMesh.node, transparentMesh.modelMatrix);
        }

        const sortedIds = Array.from(footprints.keys()).sort();

        for (const id of sortedIds) {
            const {node, mvp} = footprints.get(id);
            drawFootprint(painter, layer, node, mvp);
        }
    }

    drawSortedMeshes(painter, layer, transparentMeshes, opaqueMeshes, modelParametersVector);

    cleanup();
}

function drawSortedMeshes(painter: Painter, layer: ModelStyleLayer, transparentMeshes: Array<SortedMesh>, opaqueMeshes: Array<SortedMesh>, modelParametersVector: Array<ModelParameters>) {
    let needsStencilClear = false;

    // Render opaque meshes first.
    // The per-feature model-opacity can be less than 1.0 even for opaque meshes.
    // In that case we need to render one extra pass to write into the depth buffer.
    for (const opaqueMesh of opaqueMeshes) {
        if (opaqueMesh.modelOpacity !== 1.0) {
            // If we have layer opacity draw with two passes opaque meshes
            drawMesh(opaqueMesh, painter, layer, modelParametersVector[opaqueMesh.modelIndex], StencilMode.disabled, ColorMode.disabled);
            needsStencilClear = true;
        }
    }
    for (const opaqueMesh of opaqueMeshes) {
        if (opaqueMesh.modelOpacity !== 1.0) {
            drawMesh(opaqueMesh, painter, layer, modelParametersVector[opaqueMesh.modelIndex], painter.stencilModeFor3D(), painter.colorModeForRenderPass());
        } else {
            drawMesh(opaqueMesh, painter, layer, modelParametersVector[opaqueMesh.modelIndex], StencilMode.disabled, painter.colorModeForRenderPass());
        }
    }

    if (needsStencilClear) {
        painter.resetStencilClippingMasks();
    }

    // Draw transparent sorted meshes
    const additiveBlending = ColorMode.additive;
    for (const transparentMesh of transparentMeshes) {
        drawMesh(transparentMesh, painter, layer, modelParametersVector[transparentMesh.modelIndex], StencilMode.disabled, transparentMesh.isLightMesh ? additiveBlending : painter.colorModeForRenderPass());
    }
}

// If terrain changes, update elevations (baked in translation).
function updateModelBucketsElevation(painter: Painter, bucket: ModelBucket, bucketTileID: OverscaledTileID): boolean {
    let exaggeration = painter.terrain ? painter.terrain.exaggeration() : 0;
    let dem: DEMSampler | null | undefined;
    if (painter.terrain && exaggeration > 0) {
        const terrain = painter.terrain;
        const demTile = terrain.findDEMTileFor(bucketTileID);
        if (demTile && demTile.dem) {
            dem = DEMSampler.create(terrain, bucketTileID, demTile);
        } else {
            exaggeration = 0;
        }
    }

    if (exaggeration === 0) {
        bucket.terrainElevationMin = 0;
        bucket.terrainElevationMax = 0;
    }

    if (exaggeration === bucket.validForExaggeration &&
        (exaggeration === 0 || (dem && dem._demTile && dem._demTile.tileID === bucket.validForDEMTile.id && dem._dem._timestamp === bucket.validForDEMTile.timestamp))) {
        return false;
    }

    let elevationMin: number | null | undefined;
    let elevationMax: number | null | undefined;

    for (const modelId in bucket.instancesPerModel) {
        const instances = bucket.instancesPerModel[modelId];
        assert(instances.instancedDataArray.bytesPerElement === 64);
        for (let i = 0; i < instances.instancedDataArray.length; ++i) {
            const x = instances.instancedDataArray.float32[i * 16] | 0;
            const y = instances.instancedDataArray.float32[i * 16 + 1] | 0;
            const elevation = (dem ? exaggeration * dem.getElevationAt(x, y, true, true) : 0) + instances.instancesEvaluatedElevation[i];
            instances.instancedDataArray.float32[i * 16 + 6] = elevation;
            elevationMin = elevationMin ? Math.min(bucket.terrainElevationMin, elevation) : elevation;
            elevationMax = elevationMax ? Math.max(bucket.terrainElevationMax, elevation) : elevation;
        }
    }

    bucket.terrainElevationMin = elevationMin ? elevationMin : 0;
    bucket.terrainElevationMax = elevationMax ? elevationMax : 0;

    bucket.validForExaggeration = exaggeration;
    bucket.validForDEMTile = dem && dem._demTile ? {id: dem._demTile.tileID, timestamp: dem._dem._timestamp} : {id: undefined, timestamp: 0};

    return true;
}

function updateModelBucketData(painter: Painter, bucket: ModelBucket, bucketTileID: OverscaledTileID) {
    const bucketContentsUpdatedByZoom = bucket.updateZoomBasedPaintProperties();
    const bucketContentsUpdatedByElevation = updateModelBucketsElevation(painter, bucket, bucketTileID);

    if (bucketContentsUpdatedByZoom || bucketContentsUpdatedByElevation) {
        bucket.uploaded = false;
        bucket.upload(painter.context);
    }
}

// preallocate structure used to reduce re-allocation during rendering and flow checks
const renderData: RenderData = {
    shadowUniformsInitialized: false,
    useSingleShadowCascade: false,
    tileMatrix: new Float64Array(16),
    shadowTileMatrix: new Float32Array(16),
    aabb: new Aabb([0, 0, 0], [EXTENT, EXTENT, 0])
};

function calculateTileZoom(id: OverscaledTileID, tr: Transform): number {
    const tiles = 1 << id.canonical.z;
    const cameraPos = tr.getFreeCameraOptions().position;
    const elevation = tr.elevation;

    // Compute tile zoom from the distance between the camera and
    // the closest point on either tile's bottom plane or on a plane
    // elevated to center altitude, whichever is higher. Using center altitude
    // allows us to compensate tall tiles that have high variance in
    // instance placement on z-axis.
    const minx = id.canonical.x / tiles;
    const maxx = (id.canonical.x + 1) / tiles;
    const miny = id.canonical.y / tiles;
    const maxy = (id.canonical.y + 1) / tiles;
    let height = tr._centerAltitude;

    if (elevation) {
        const minmax = elevation.getMinMaxForTile(id);

        if (minmax && minmax.max > height) {
            height = minmax.max;
        }
    }

    const distx = clamp(cameraPos.x, minx, maxx) - cameraPos.x;
    const disty = clamp(cameraPos.y, miny, maxy) - cameraPos.y;
    const distz = mercatorZfromAltitude(height, tr.center.lat) - cameraPos.z;

    return tr._zoomFromMercatorZ(Math.sqrt(distx * distx + disty * disty + distz * distz));
}

function drawVectorLayerModels(painter: Painter, source: SourceCache, layer: ModelStyleLayer, coords: Array<OverscaledTileID>, scope: string) {
    const tr = painter.transform;
    const isGlobe = tr.projection.name === 'globe';

    const mercCameraPos = tr.getFreeCameraOptions().position;
    if (!painter.modelManager) return;
    const modelManager = painter.modelManager;
    layer.modelManager = modelManager;
    const shadowRenderer = painter.shadowRenderer;

    if (!Object.hasOwn(layer._unevaluatedLayout._values, 'model-id')) { return; }

    const modelIdUnevaluatedProperty = layer._unevaluatedLayout._values['model-id'];

    const evaluationParameters = {...layer.layout.get("model-id").parameters} as EvaluationParameters;

    const layerIndex = painter.style.order.indexOf(layer.fqid);

    const layerOpacity = layer.paint.get('model-opacity').constantOr(1.0);

    for (const coord of coords) {
        const tile = source.getTile(coord);
        const bucket = tile.getBucket(layer) as ModelBucket | null | undefined;
        if (!bucket || bucket.projection.name !== tr.projection.name) continue;
        const modelUris = bucket.getModelUris();
        if (modelUris && !bucket.modelsRequested) {
            modelManager.addModelsFromBucket(modelUris, scope);
            bucket.modelsRequested = true;
        }

        if (!isGlobe) {
            const tileZoom = calculateTileZoom(coord, tr);
            evaluationParameters.zoom = tileZoom;
        } else {
            evaluationParameters.zoom = coord.overscaledZ;
        }
        const modelIdProperty = modelIdUnevaluatedProperty.possiblyEvaluate(evaluationParameters);
        updateModelBucketData(painter, bucket, coord);

        renderData.shadowUniformsInitialized = false;
        renderData.useSingleShadowCascade = !!shadowRenderer && shadowRenderer.getMaxCascadeForTile(coord.toUnwrapped()) === 0;
        if (painter.renderPass === 'shadow' && shadowRenderer) {
            if (painter.currentShadowCascade === 1 && bucket.isInsideFirstShadowMapFrustum) continue;

            const tileMatrix = tr.calculatePosMatrix(coord.toUnwrapped(), tr.worldSize);
            renderData.tileMatrix.set(tileMatrix);
            renderData.shadowTileMatrix.set(shadowRenderer.calculateShadowPassMatrixFromMatrix(tileMatrix));
            renderData.aabb.min = [0, 0, 0];
            renderData.aabb.max[0] = renderData.aabb.max[1] = EXTENT;
            renderData.aabb.max[2] = 0;
            if (calculateTileShadowPassCulling(bucket, renderData, painter, layer.scope)) continue;
        }

        // camera position in the tile coordinates
        const tiles = 1 << coord.canonical.z;
        const cameraPos: [number, number, number] = [
            ((mercCameraPos.x - coord.wrap) * tiles - coord.canonical.x) * EXTENT,
            (mercCameraPos.y * tiles - coord.canonical.y) * EXTENT,
            mercCameraPos.z * tiles * EXTENT
        ];

        const clippable = painter.conflationActive && Object.keys(bucket.instancesPerModel).length > 0 && painter.style.isLayerClipped(layer, source.getSource());
        if (clippable) {
            if (bucket.updateReplacement(coord, painter.replacementSource, layerIndex, layer.scope)) {
                bucket.uploaded = false;
                bucket.upload(painter.context);
            }
        }

        let modelIndex = 0;
        const transparentMeshes = new Array<SortedMesh>();
        const opaqueMeshes = new Array<SortedMesh>();
        const modelParametersVector = new Array<ModelParameters>();

        for (let modelId in bucket.instancesPerModel) {
            // From effective tile zoom (distance to camera) and calculate model to use.
            const modelInstances = bucket.instancesPerModel[modelId];
            if (modelInstances.features.length > 0 && !isGlobe) {
                modelId = modelIdProperty.evaluate(modelInstances.features[0].feature, {});
            }

            const model = modelManager.getModel(modelId, scope);
            if (!model && !modelManager.hasURLBeenRequested(modelId) && !bucket.modelUris.includes(modelId)) {
                // We are asking for a model that's not yet on this bucket's model list
                bucket.modelUris.push(modelId);
                bucket.modelsRequested = false;
            }
            if (!model || !model.uploaded) continue;

            if (isGlobe) {
                const cameraPosGlobe = vec3.scale([], [mercCameraPos.x, mercCameraPos.y, mercCameraPos.z], painter.transform.worldSize);
                vec3.negate(cameraPosGlobe, cameraPosGlobe);

                for (let instanceIndex = 0; instanceIndex < modelInstances.instancedDataArray.length; ++instanceIndex) {
                    const rotation = [0, 0, 0] as vec3;
                    const scale = [1, 1, 1] as vec3;
                    const rotationQuat = quat.create();

                    const tilePosition = modelInstances.tileCoordinatesForInstance(instanceIndex);
                    const instanceTransform = modelInstances.transformForInstance(instanceIndex);
                    mat4.getScaling(scale, instanceTransform);
                    mat4.getRotation(rotationQuat, instanceTransform);
                    quat.getAxisAngle(rotation, rotationQuat);
                    const translation = modelInstances.translationForInstance(instanceIndex);

                    const position = new LngLat(0, 0);
                    tileToLngLat(bucket.canonical, position, tilePosition.x, tilePosition.y);

                    const modelMatrix = mat4.create();
                    calculateModelMatrix(modelMatrix, model, painter.transform, position, rotation, scale, translation, true, false, false);

                    const colorMix = modelInstances.colorForInstance(instanceIndex);

                    // compute model parameters matrices
                    const negCameraPosMatrix = mat4.fromTranslation([], cameraPosGlobe);
                    const modelMetersPerPixel = getMetersPerPixelAtLatitude(position.lat, painter.transform.zoom);
                    const modelPixelsPerMeter = 1.0 / modelMetersPerPixel;
                    const zScaleMatrix = mat4.fromScaling([], [1.0, 1.0, modelPixelsPerMeter]);
                    const modelParameters = {zScaleMatrix, negCameraPosMatrix};
                    modelParametersVector.push(modelParameters);

                    for (const node of model.nodes) {
                        prepareMeshes(painter, node, modelMatrix, painter.transform.expandedFarZProjMatrix, modelIndex, transparentMeshes, opaqueMeshes, model.materialOverrides, layerOpacity, colorMix);
                    }
                    ++modelIndex;
                }
            } else {
                for (const node of model.nodes) {
                    drawInstancedNode(painter, layer, node, modelInstances, cameraPos, coord, renderData);
                }
            }
        }

        if (isGlobe) {
            // Draw opaque and transparent meshes. Transparent meshes are not sorted in this case to ensure parity with native implementation.
            if (painter.renderPass === 'shadow') {
                for (const opaqueMesh of opaqueMeshes) {
                    drawShadowCaster(opaqueMesh.mesh, opaqueMesh.nodeModelMatrix, painter, layer);
                }
                // Draw transparent sorted meshes
                for (const transparentMesh of transparentMeshes) {
                    drawShadowCaster(transparentMesh.mesh, transparentMesh.nodeModelMatrix, painter, layer);
                }
            } else {
                drawSortedMeshes(painter, layer, transparentMeshes, opaqueMeshes, modelParametersVector);
            }

        }
    }
}

const minimumInstanceCount = 20;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function drawInstancedNode(painter: Painter, layer: ModelStyleLayer, node: ModelNode, modelInstances: any, cameraPos: [number, number, number], coord: OverscaledTileID, renderData: RenderData) {
    const context = painter.context;
    const isShadowPass = painter.renderPass === 'shadow';
    const shadowRenderer = painter.shadowRenderer;
    const depthMode = isShadowPass && shadowRenderer ? shadowRenderer.getShadowPassDepthMode() : new DepthMode(context.gl.LEQUAL, DepthMode.ReadWrite, painter.depthRangeFor3D);
    const affectedByFog = painter.isTileAffectedByFog(coord);

    const isGlobe = painter.transform.projection.name === 'globe';

    if (node.meshes) {
        for (const mesh of node.meshes) {
            const definesValues = isGlobe ? [] : ['MODEL_POSITION_ON_GPU'];
            const dynamicBuffers = [];
            let program: Program<ModelUniformsType | ModelDepthUniformsType>;
            let uniformValues: UniformValues<ModelUniformsType | ModelDepthUniformsType>;
            let colorMode: ColorMode;

            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            const useInstancing = !isGlobe && (modelInstances.instancedDataArray.length > minimumInstanceCount);

            if (useInstancing) {
                definesValues.push('INSTANCED_ARRAYS');
            }

            const cutoffParams = getCutoffParams(painter, layer.paint.get('model-cutoff-fade-range'));
            if (cutoffParams.shouldRenderCutoff) {
                definesValues.push('RENDER_CUTOFF');
            }
            if (isShadowPass && shadowRenderer) {
                program = painter.getOrCreateProgram('modelDepth', {defines: (definesValues as DynamicDefinesType[])});
                uniformValues = modelDepthUniformValues(renderData.shadowTileMatrix, renderData.shadowTileMatrix, node.globalMatrix);
                colorMode = ColorMode.disabled;
            } else {

                const ignoreLut = layer.paint.get('model-color-use-theme').constantOr('default') === 'none';
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                setupMeshDraw(definesValues, dynamicBuffers, mesh, painter, ignoreLut ? null : layer.lut);
                program = painter.getOrCreateProgram('model', {defines: (definesValues as DynamicDefinesType[]), overrideFog: affectedByFog});
                const material = mesh.material;
                const pbr = material.pbrMetallicRoughness;
                const layerOpacity = layer.paint.get('model-opacity').constantOr(1.0);

                const emissiveStrength = layer.paint.get('model-emissive-strength').constantOr(0.0);
                uniformValues = modelUniformValues(
                    coord.expandedProjMatrix,
                    node.globalMatrix,
                    instancedNormalMatrixPlaceholder,
                    null,
                    painter,
                    layerOpacity,
                    pbr.baseColorFactor,
                    material.emissiveFactor,
                    pbr.metallicFactor,
                    pbr.roughnessFactor,
                    material,
                    emissiveStrength,
                    layer,
                    cameraPos,
                    undefined,
                    undefined,
                    undefined,
                    1.0,
                    program.fixedDefines.includes('LIGHTING_3D_MODE')
                );
                if (shadowRenderer) {
                    if (!renderData.shadowUniformsInitialized) {
                        shadowRenderer.setupShadows(coord.toUnwrapped(), program, 'model-tile');
                        renderData.shadowUniformsInitialized = true;
                    } else {
                        program.setShadowUniformValues(context, shadowRenderer.getShadowUniformValues());
                    }
                }

                const needsBlending = cutoffParams.shouldRenderCutoff || layerOpacity < 1.0 || material.alphaMode !== 'OPAQUE';
                colorMode = needsBlending ? ColorMode.alphaBlended : ColorMode.unblended;
            }

            painter.uploadCommonUniforms(context, program, coord.toUnwrapped(), null, cutoffParams);

            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            assert(modelInstances.instancedDataArray.bytesPerElement === 64);
            const cullFaceMode = mesh.material.doubleSided ? CullFaceMode.disabled : CullFaceMode.backCCW;
            if (useInstancing) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                dynamicBuffers.push(modelInstances.instancedDataBuffer);
                program.draw(painter, context.gl.TRIANGLES, depthMode, StencilMode.disabled, colorMode, cullFaceMode,
                uniformValues, layer.id, mesh.vertexBuffer, mesh.indexBuffer, mesh.segments, layer.paint, painter.transform.zoom,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
                undefined, dynamicBuffers, modelInstances.instancedDataArray.length);
            } else {
                const instanceUniform = isShadowPass ? "u_instance" : "u_normal_matrix";
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                for (let i = 0; i < modelInstances.instancedDataArray.length; ++i) {
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                    uniformValues[instanceUniform] = new Float32Array(modelInstances.instancedDataArray.arrayBuffer, i * 64, 16);
                    program.draw(painter, context.gl.TRIANGLES, depthMode, StencilMode.disabled, colorMode, cullFaceMode,
                    uniformValues, layer.id, mesh.vertexBuffer, mesh.indexBuffer, mesh.segments, layer.paint, painter.transform.zoom,
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                    undefined, dynamicBuffers);
                }
            }
        }
    }
    if (node.children) {
        for (const child of node.children) {
            drawInstancedNode(painter, layer, child, modelInstances, cameraPos, coord, renderData);
        }
    }
}

function prepareBatched(painter: Painter, source: SourceCache, layer: ModelStyleLayer, coords: Array<OverscaledTileID>) {
    const exaggeration = painter.terrain ? painter.terrain.exaggeration() : 0;
    const zoom = painter.transform.zoom;
    for (const coord of coords) {
        const tile = source.getTile(coord);
        const bucket = tile.getBucket(layer) as Tiled3dModelBucket | null | undefined;
        if (!bucket) continue;
        bucket.setFilter(layer.filter);
        // Conflation
        if (painter.conflationActive) bucket.updateReplacement(coord, painter.replacementSource);
        bucket.evaluateTransform(painter, layer);
        // Compute elevation
        if (painter.terrain && exaggeration > 0) {
            bucket.elevationUpdate(painter.terrain, exaggeration, coord, layer.source);
        }
        if (bucket.needsReEvaluation(painter, zoom, layer)) {
            bucket.evaluate(layer);
        }
    }
}

function updateModelLod(nodeInfo: Tiled3dModelFeature, distanceToCamera: number, dtMs: number, switchDistance: number, fadeDuration: number) {
    const node = nodeInfo.node;
    if (node.lodMeshes && node.lodMeshes.length > 0) {
        if (nodeInfo.targetLod < 0) {
            // First time this node is LOD-evaluated: snap to the correct level
            // instantly to avoid fading from a state the user never saw.
            nodeInfo.targetLod = distanceToCamera > switchDistance ? 1.0 : 0.0;
        } else {
            const timePassed = dtMs / 1000.0; // milliseconds to seconds
            // Convert seconds passed into LOD progress per frame. A duration of 0 means snap instantly.
            const fadeStep = fadeDuration > 0.0 ? timePassed / fadeDuration : 1.0;
            if (distanceToCamera > switchDistance) {
                nodeInfo.targetLod = clamp(nodeInfo.targetLod + fadeStep, 0.0, 1.0);
            } else {
                nodeInfo.targetLod = clamp(nodeInfo.targetLod - fadeStep, 0.0, 1.0);
            }
        }
    } else {
        nodeInfo.targetLod = 0;
    }
}

function drawBatchedModels(painter: Painter, source: SourceCache, layer: ModelStyleLayer, coords: Array<OverscaledTileID>) {
    layer.resetLayerRenderingStats(painter);
    const context = painter.context;
    const tr = painter.transform;
    const fog = painter.style.fog;
    const shadowRenderer = painter.shadowRenderer;
    if (tr.projection.name !== 'mercator') {
        warnOnce(`Drawing 3D landmark models for ${tr.projection.name} projection is not yet implemented`);
        return;
    }

    const mercCameraPos = painter.transform.getFreeCameraOptions().position;
    const cameraPos = vec3.scale([], [mercCameraPos.x, mercCameraPos.y, mercCameraPos.z], painter.transform.worldSize);
    const negCameraPos = vec3.negate([], cameraPos);
    // compute model parameters matrices
    const negCameraPosMatrix = mat4.fromTranslation([], negCameraPos);
    const metersPerPixel = getMetersPerPixelAtLatitude(tr.center.lat, tr.zoom);
    const pixelsPerMeter = 1.0 / metersPerPixel;
    const zScaleMatrix = mat4.fromScaling([], [1.0, 1.0, pixelsPerMeter]);
    // Precomputed once so the per-node lighting path can collapse two muls into one:
    // (negCam * zScale) * tileModelMatrix == negCam * (zScale * tileModelMatrix).
    const negCameraPosZScaleMatrix = mat4.multiply([], negCameraPosMatrix, zScaleMatrix);
    const layerOpacity = layer.paint.get('model-opacity').constantOr(1.0);

    const depthModeRW = new DepthMode(context.gl.LEQUAL, DepthMode.ReadWrite, painter.depthRangeFor3D);
    const depthModeRO = new DepthMode(context.gl.LEQUAL, DepthMode.ReadOnly, painter.depthRangeFor3D);

    const aabb = new Aabb([Infinity, Infinity, Infinity], [-Infinity, -Infinity, -Infinity]);
    const isShadowPass = painter.renderPass === 'shadow';
    const isLightBeamPass = painter.renderPass === 'light-beam';
    const ignoreLut = layer.paint.get('model-color-use-theme').constantOr('default') === 'none';
    const frustum = isShadowPass && shadowRenderer ? shadowRenderer.getCurrentCascadeFrustum() : tr.getFrustum(tr.scaleZoom(tr.worldSize));

    const frontCutoffParams = layer.paint.get('model-front-cutoff');
    const frontCutoffEnabled = frontCutoffParams[2] < 1.0;

    const cutoffParams = getCutoffParams(painter, layer.paint.get('model-cutoff-fade-range'));

    const stats = layer.getLayerRenderingStats();
    const drawTiles = function () {
        // Keyed by ID to deduplicate footprints across tiles
        const footprints = new Map<string, {node: ModelNode, mvp: mat4}>();

        let start: number;
        let end: number;
        let step: number;
        // When front cutoff is enabled the tiles are iterated in back to front order
        if (frontCutoffEnabled) {
            start = coords.length - 1;
            end = -1;
            step = -1;
        } else {
            start = 0;
            end = coords.length;
            step = 1;
        }

        const cameraPosTileCoord = vec3.create();
        const cameraPointTileCoord = new Point(0.0, 0.0);

        for (let i = start; i !== end; i += step) {
            const coord = coords[i];
            const tile = source.getTile(coord);
            const bucket = tile.getBucket(layer) as Tiled3dModelBucket | null | undefined;
            if (!bucket || !bucket.uploaded) continue;

            let singleCascade = false;
            if (shadowRenderer) {
                singleCascade = shadowRenderer.getMaxCascadeForTile(coord.toUnwrapped()) === 0;
            }
            const tileMatrix = tr.calculatePosMatrix(coord.toUnwrapped(), tr.worldSize);
            const hasMapboxFeatures = !!(bucket.modelTraits & ModelTraits.HasMapboxMeshFeatures);
            mat4.multiply(lightingTileMatrix, negCameraPosZScaleMatrix, tileMatrix);
            mat4.multiply(projTileMatrix, tr.expandedFarZProjMatrix, tileMatrix);
            // Pre-bake the tile-level normal matrix once. Per-node it just needs a column scale
            // (1/evaluatedScale with a Y-flip baked in) — no per-node mat4.invert.
            mat4.invert(lightingTileNormalMatrix, lightingTileMatrix);
            mat4.transpose(lightingTileNormalMatrix, lightingTileNormalMatrix);
            // Per-tile cache of cascade.matrix * tileMatrix; per-node setupShadowsFromTileCache
            // applies translate + maybe scale instead of doing a fresh cascade-mul.
            const cascadeTileMatrices = !isShadowPass && shadowRenderer && shadowRenderer.enabled ?
                shadowRenderer.computeCascadeTileMatrices(tileMatrix) : null;

            if (!isShadowPass && frontCutoffEnabled) {
                // normalMatrixScratch is dead at this point — only written inside the post-sort node loop below.
                mat4.invert(normalMatrixScratch, tileMatrix);
                vec3.transformMat4(cameraPosTileCoord, cameraPos, normalMatrixScratch);
                cameraPointTileCoord.x = cameraPosTileCoord[0];
                cameraPointTileCoord.y = cameraPosTileCoord[1];
            }

            const sortedNodes: Array<SortedNode> = [];
            bucket.setFilter(layer.filter);
            for (const nodeInfo of bucket.getNodesInfo()) {
                if (nodeInfo.hiddenByReplacement) continue;
                if (!nodeInfo.node.meshes) continue;

                const node = nodeInfo.node;
                let elevation = 0;
                if (painter.terrain && node.elevation) {
                    elevation = node.elevation * painter.terrain.exaggeration();
                }

                const calculateNodeAabb = () => {
                    const localBounds = nodeInfo.aabb;
                    const min = aabb.min;
                    const max = aabb.max;
                    min[0] = localBounds.min[0];
                    min[1] = localBounds.min[1];
                    min[2] = localBounds.min[2] + elevation;
                    max[0] = localBounds.max[0];
                    max[1] = localBounds.max[1];
                    max[2] = localBounds.max[2] + elevation;
                    vec3.transformMat4(min, min, tileMatrix);
                    vec3.transformMat4(max, max, tileMatrix);
                    return aabb;
                };
                const nodeAabb = calculateNodeAabb();

                const scale = nodeInfo.evaluatedScale;
                if (scale[0] <= 1 && scale[1] <= 1 && scale[2] <= 1 && nodeAabb.intersects(frustum) === 0) {
                    // While it is possible to use arbitrary scale for landmarks, it is highly unlikely
                    // and frustum culling optimization could be skipped in that case.
                    continue;
                }

                if (!isShadowPass) {
                    const min = nodeAabb.min, max = nodeAabb.max;
                    lodNodeCenterScratch[0] = (min[0] + max[0]) * 0.5;
                    lodNodeCenterScratch[1] = (min[1] + max[1]) * 0.5;
                    lodNodeCenterScratch[2] = (min[2] + max[2]) * 0.5;
                    const distanceToCamera = vec3.distance(cameraPos, lodNodeCenterScratch) * metersPerPixel;

                    const overrideDistance = painter._debugParams.lodSwitchDistance;
                    const hasOverride = overrideDistance >= 0;

                    if (hasOverride && overrideDistance >= 9999) {
                        // LOD disabled: snap to full detail regardless of distance.
                        nodeInfo.targetLod = 0;
                    } else {
                        let switchAtDistance: number;
                        if (hasOverride) {
                            switchAtDistance = overrideDistance;
                        } else {
                            // Size-based heuristics: derive switch distance from AABB dimensions,
                            // matching gl-native constants (minDist=2000, range=3000).
                            const physicalHeight = (max[2] - min[2]) * metersPerPixel * scale[2];
                            const physicalWidth = Math.max(max[0] - min[0], max[1] - min[1]) * metersPerPixel * Math.max(scale[0], scale[1]);
                            let sizeFactor: number;
                            if (physicalHeight >= 30) {
                                sizeFactor = 1.0;   // large
                            } else if (physicalWidth >= 80) {
                                sizeFactor = 0.5;   // flat (wide but not tall)
                            } else if (Math.max(physicalHeight, physicalWidth) >= 20) {
                                sizeFactor = 0.25;  // small
                            } else {
                                sizeFactor = 0.0;   // tiny
                            }
                            switchAtDistance = 2000 + sizeFactor * 3000;
                        }
                        // Rendering can get paused and thus the LOD transition may stop. Therefore don't use the full time-step,
                        // such that when rendering is resumed, the transition smoothly continues.
                        updateModelLod(nodeInfo, distanceToCamera, Math.min(painter.frameTimeDelta, 1000 / 30), switchAtDistance, painter._debugParams.lodSwitchFadeDuration);
                    }
                }

                if (!isShadowPass && frontCutoffEnabled) {
                    const opacityChangePerFrame = 1.0 / 6.0; // this is not driving animation evaluation, but is updated on change
                    if (cameraPos[0] > nodeAabb.min[0] && cameraPos[0] < nodeAabb.max[0] &&
                        cameraPos[1] > nodeAabb.min[1] && cameraPos[1] < nodeAabb.max[1] &&
                        cameraPos[2] * metersPerPixel < nodeAabb.max[2] &&
                        node.footprint && pointInFootprint(cameraPointTileCoord, node.footprint)) {
                        nodeInfo.cameraCollisionOpacity = Math.max(nodeInfo.cameraCollisionOpacity - opacityChangePerFrame, 0.0);
                    } else {
                        nodeInfo.cameraCollisionOpacity = Math.min(1.0, nodeInfo.cameraCollisionOpacity + opacityChangePerFrame);
                    }
                }

                const tileUnitsPerMeter = 1.0 / tileToMeter(coord.canonical);

                const anchorX = node.anchor ? node.anchor[0] : 0;
                const anchorY = node.anchor ? node.anchor[1] : 0;

                const tileTranslation: vec3 = [
                    anchorX * (scale[0] - 1) + nodeInfo.evaluatedTranslation[0] * tileUnitsPerMeter,
                    anchorY * (scale[1] - 1) + nodeInfo.evaluatedTranslation[1] * tileUnitsPerMeter,
                    elevation + nodeInfo.evaluatedTranslation[2]];
                const tileModelMatrix = applyTileTransform(new Float64Array(16), tileMatrix, tileTranslation, scale);

                // keep model and nodemodel matrices separate for rendering door lights
                const nodeModelMatrix = mat4.multiply([], tileModelMatrix, node.globalMatrix);
                const wvpForNode = mat4.multiply([], tr.expandedFarZProjMatrix, nodeModelMatrix);
                // Only the z (depth) component of the projected anchor is used below; computing
                // the full vec4.transformMat4 would do 3 unused mul-adds and allocate two arrays.
                const depth = wvpForNode[2] * anchorX + wvpForNode[6] * anchorY + wvpForNode[10] * elevation + wvpForNode[14];

                node.hidden = false;
                let opacity = layerOpacity;
                if (!isShadowPass) {
                    if (frontCutoffEnabled) {
                        opacity *= nodeInfo.cameraCollisionOpacity;
                        opacity *= calculateFrontCutoffOpacity(tileModelMatrix, tr, nodeInfo.aabb, frontCutoffParams);
                    }

                    opacity *= calculateFarCutoffOpacity(cutoffParams, depth);
                }
                if (opacity === 0.0) {
                    node.hidden = true;
                    continue;
                }

                const sortedNode: SortedNode = {
                    nodeInfo,
                    depth,
                    opacity,
                    wvpForNode,
                    nodeModelMatrix,
                    tileModelMatrix,
                    tileTranslation
                };

                sortedNodes.push(sortedNode);
            }

            if (!isShadowPass) {
                // Sort nodes. Opaque nodes first in front to back order. Then non-opaque nodes in back to front order.
                sortedNodes.sort((a, b) => {
                    // Front to back order for opaque nodes, and for all nodes when front cutoff is disabled
                    if (!frontCutoffEnabled || (a.opacity === 1.0 && b.opacity === 1.0)) {
                        return a.depth < b.depth ? -1 : 1;
                    }
                    if (a.opacity === 1.0) {
                        return -1;
                    }
                    if (b.opacity === 1.0) {
                        return 1;
                    }

                    // Back to front order for non-opaque nodes
                    return a.depth > b.depth ? -1 : 1;
                });
            }

            for (const sortedNode of sortedNodes) {
                const nodeInfo = sortedNode.nodeInfo;
                const node = nodeInfo.node;
                // Cascade matrices and shadow texture binds depend on (worldMatrix, normalOffset),
                // both of which only change between meshes when normalOffset flips. Track the last
                // value so we can skip the redundant work for subsequent meshes of this node.
                let lastShadowsNormalOffset: boolean | null = null;

                if (painter._debugParams.show3DModelFootprints && node.footprint) {
                    const id = node.id || node.name || 'footprint';
                    if (!footprints.has(id)) {
                        // Footprints are consumed after the tile loop, so this needs its own allocation.
                        const mvp = applyTileTransform(new Float64Array(16), projTileMatrix, sortedNode.tileTranslation, sortedNode.nodeInfo.evaluatedScale);
                        footprints.set(id, {node, mvp});
                    }
                }

                // negCameraPosZScale * tileMatrix is precomputed once per tile; rebuild the per-node
                // lighting matrix on top of it via translate/scale instead of a full mat4.multiply.
                const evaluatedScale = sortedNode.nodeInfo.evaluatedScale;
                applyTileTransform(lightingMatrixScratch, lightingTileMatrix, sortedNode.tileTranslation, evaluatedScale);

                // Normal matrix derives from the tile-level transpose-of-inverse with a single column scale:
                // upper-3x3 of (lightingTileMatrix * T(t) * S(s))^-T equals upper-3x3(lightingTileMatrix^-T) * diag(1/s).
                // Y-flip is baked into the same scale.
                normalScaleScratch[0] = 1 / evaluatedScale[0];
                normalScaleScratch[1] = -1 / evaluatedScale[1];
                normalScaleScratch[2] = 1 / evaluatedScale[2];
                mat4.scale(normalMatrixScratch, lightingTileNormalMatrix, normalScaleScratch);

                // lighting matrix should take node.matrix into account
                mat4.multiply(lightingMatrixScratch, lightingMatrixScratch, node.globalMatrix);

                const emissiveStrength = hasMapboxFeatures ? 0.0 : nodeInfo.emissiveStrength;

                const targetLod = nodeInfo.targetLod;
                const hasLod = node.lodMeshes && node.lodMeshes.length > 0;
                const inTransition = hasLod && targetLod > 0.0 && targetLod < 1.0;

                if (isShadowPass && hasLod && Math.round(targetLod) === 1) {
                    continue;
                }

                // Hoisted per-node: inputs (nodeModelMatrix, transform, current cascade) are mesh-invariant.
                const nodeFogMatrix = (!isShadowPass && fog) ?
                    fogMatrixForModel(fogMatrixScratch, sortedNode.nodeModelMatrix, painter.transform) : null;
                const nodeShadowPassMatrix = (isShadowPass && shadowRenderer) ?
                    shadowRenderer.calculateShadowPassMatrixFromMatrix(sortedNode.nodeModelMatrix) : null;

                const passes = inTransition ? 2 : 1;
                const useLodWhenNotTransitioning = hasLod && Math.round(targetLod) === 1;
                for (let pass = 0; pass < passes; ++pass) {
                    const isLodMeshes = inTransition ? pass === 1 : useLodWhenNotTransitioning;
                    const meshes = isLodMeshes ? node.lodMeshes : node.meshes;
                    const threshold = inTransition ? (isLodMeshes ? -targetLod : 1.0 - targetLod) : 1.0;
                    for (let i = 0; i < meshes.length; ++i) {
                        const mesh = meshes[i];
                        // Light mesh only exists in the primary mesh array, not LOD meshes
                        const isLight = !isLodMeshes && i === node.lightMeshIndex;
                        let worldViewProjection = sortedNode.wvpForNode;
                        if (isLight) {
                            if (!isLightBeamPass && !painter.terrain && painter.shadowRenderer) {
                                if (painter.currentLayer < painter.firstLightBeamLayer) {
                                    painter.firstLightBeamLayer = painter.currentLayer;
                                }
                                continue;
                            }
                            // Lights come in tilespace, so wvp is built from the tile matrix without node transforms.
                            // Scratch is safe to reuse across light meshes — program.draw uploads synchronously.
                            worldViewProjection = applyTileTransform(wvpTileScratch, projTileMatrix, sortedNode.tileTranslation, sortedNode.nodeInfo.evaluatedScale);
                        } else if (isLightBeamPass) {
                            continue;
                        }

                        const programOptions: CreateProgramParams = {
                            defines: []
                        };
                        const dynamicBuffers: (VertexBuffer | null | undefined)[] | undefined = [];

                        const useNormalOffset = !!mesh.normalBuffer;
                        if (!isShadowPass && shadowRenderer) {
                            // setupMeshDraw reads this back to push the NORMAL_OFFSET define.
                            shadowRenderer.useNormalOffset = useNormalOffset;
                        }

                        const partStyleUBO = partStyleUBOForMesh(nodeInfo, mesh);
                        setupMeshDraw((programOptions.defines), dynamicBuffers, mesh, painter, ignoreLut ? null : layer.lut, partStyleUBO);
                        if (!hasMapboxFeatures) {
                            programOptions.defines.push('DIFFUSE_SHADED');
                        }

                        if (singleCascade) {
                            programOptions.defines.push('SHADOWS_SINGLE_CASCADE');
                        }

                        if (stats) {
                            if (!isShadowPass) {
                                stats.numRenderedVerticesInTransparentPass += mesh.vertexArray.length;
                            } else {
                                stats.numRenderedVerticesInShadowPass += mesh.vertexArray.length;
                            }
                        }

                        if (isShadowPass) {
                            drawShadowCaster(mesh, sortedNode.nodeModelMatrix, painter, layer, nodeShadowPassMatrix);
                            continue;
                        }

                        if (fog && tr.projection.name !== 'globe') {
                            const min = mesh.aabb.min;
                            const max = mesh.aabb.max;
                            const [minOpacity, maxOpacity] = fog.getOpacityForBounds(nodeFogMatrix, min[0], min[1], max[0], max[1]);
                            programOptions.overrideFog = minOpacity >= FOG_OPACITY_THRESHOLD || maxOpacity >= FOG_OPACITY_THRESHOLD;
                        }

                        const material = mesh.material;
                        let occlusionTextureTransform: [number, number, number, number] | undefined;
                        // Handle Texture transform
                        if (material.occlusionTexture && material.occlusionTexture.offsetScale) {
                            occlusionTextureTransform = material.occlusionTexture.offsetScale;
                            programOptions.defines.push('OCCLUSION_TEXTURE_TRANSFORM');
                        }

                        if (inTransition) {
                            programOptions.defines.push('DITHERED_DISCARD');
                        }

                        const program = painter.getOrCreateProgram('model', programOptions);

                        if (!isShadowPass && shadowRenderer && shadowRenderer.enabled) {
                            // The shadow matrix does not need to include node transforms,
                            // as shadow_pos will be performing that transform in the shader
                            if (lastShadowsNormalOffset !== useNormalOffset) {
                                for (let c = 0; c < cascadeTileMatrices.length; c++) {
                                    const out = shadowLightMatrices[c] = shadowLightMatrices[c] || new Float64Array(16);
                                    applyTileTransform(out, cascadeTileMatrices[c], sortedNode.tileTranslation, sortedNode.nodeInfo.evaluatedScale);
                                }
                                shadowRenderer.setupShadowsFromCascadeMatrices(shadowLightMatrices, program, useNormalOffset);
                                lastShadowsNormalOffset = useNormalOffset;
                            } else {
                                program.setShadowUniformValues(context, shadowRenderer.getShadowUniformValues());
                            }
                        }

                        painter.uploadCommonUniforms(context, program, null, nodeFogMatrix);

                        const pbr = material.pbrMetallicRoughness;
                        // These values were taken from the tilesets used for testing
                        pbr.metallicFactor = 0.9;
                        pbr.roughnessFactor = 0.5;

                        // Set emissive strength to zero for landmarks, as it is already used embedded in the PBR buffer.
                        const uniformValues = modelUniformValues(
                                worldViewProjection,
                                lightingMatrixScratch,
                                normalMatrixScratch,
                                node.globalMatrix,
                                painter,
                                sortedNode.opacity,
                                pbr.baseColorFactor,
                                material.emissiveFactor,
                                pbr.metallicFactor,
                                pbr.roughnessFactor,
                                material,
                                emissiveStrength,
                                layer,
                                zeroCameraPos,
                                occlusionTextureTransform,
                                undefined,
                                undefined,
                                threshold,
                                program.fixedDefines.includes('LIGHTING_3D_MODE')
                        );

                        uniformValues['u_model_mesh_params'] = modelMeshParams(mesh, node, isLight, isLodMeshes);
                        if (partStyleUBO) {
                            partStyleUBO.bind(context, program.program);
                        }

                        if (!isLight && (nodeInfo.hasTranslucentParts || sortedNode.opacity < 1.0)) {

                            program.draw(painter, context.gl.TRIANGLES, depthModeRW, StencilMode.disabled, ColorMode.disabled, CullFaceMode.backCCW,
                                uniformValues, layer.id, mesh.vertexBuffer, mesh.indexBuffer, mesh.segments, layer.paint, painter.transform.zoom,

                                undefined, dynamicBuffers);
                        }

                        const meshNeedsBlending = isLight || sortedNode.opacity < 1.0 || nodeInfo.hasTranslucentParts;
                        const colorMode = meshNeedsBlending ? ColorMode.alphaBlended : ColorMode.unblended;
                        const depthMode = !isLight ? depthModeRW : depthModeRO;
                        program.draw(painter, context.gl.TRIANGLES, depthMode, StencilMode.disabled, colorMode, CullFaceMode.backCCW,
                            uniformValues, layer.id, mesh.vertexBuffer, mesh.indexBuffer, mesh.segments, layer.paint, painter.transform.zoom,

                            undefined, dynamicBuffers);
                    }
                }
            }
        }

        if (painter._debugParams.show3DModelFootprints && footprints.size > 0) {
            const sortedIds = Array.from(footprints.keys()).sort();
            for (const id of sortedIds) {
                const {node, mvp} = footprints.get(id);
                drawFootprint(painter, layer, node, mvp);
            }
        }
    };

    // Evaluate bucket and prepare for rendering
    prepareBatched(painter, source, layer, coords);

    drawTiles();
}

function calculateTileShadowPassCulling(bucket: ModelBucket, renderData: RenderData, painter: Painter, scope: string) {
    if (!painter.modelManager) return true;
    const modelManager = painter.modelManager;
    if (!painter.shadowRenderer) return true;
    const shadowRenderer = painter.shadowRenderer;
    assert(painter.renderPass === 'shadow');
    const aabb = renderData.aabb;
    let allModelsLoaded = true;
    let maxHeight = bucket.maxHeight;
    if (maxHeight === 0) {
        let maxDim = 0;
        for (const modelId in bucket.instancesPerModel) {
            const model = modelManager.getModel(modelId, scope);
            if (!model) {
                allModelsLoaded = false;
                continue;
            }
            maxDim = Math.max(maxDim, Math.max(Math.max(model.aabb.max[0], model.aabb.max[1]), model.aabb.max[2]));
        }
        maxHeight = bucket.maxScale * maxDim * 1.41 + bucket.maxVerticalOffset;
        if (allModelsLoaded) bucket.maxHeight = maxHeight;
    }
    aabb.max[2] = maxHeight;

    // Take into account bucket placement on DEM
    aabb.min[2] += bucket.terrainElevationMin;
    aabb.max[2] += bucket.terrainElevationMax;

    vec3.transformMat4(aabb.min, aabb.min, renderData.tileMatrix);
    vec3.transformMat4(aabb.max, aabb.max, renderData.tileMatrix);
    const intersection = aabb.intersects(shadowRenderer.getCurrentCascadeFrustum());
    if (painter.currentShadowCascade === 0) {
        bucket.isInsideFirstShadowMapFrustum = intersection === 2;
    }
    return intersection === 0;
}

function calculateFarCutoffOpacity(cutoffParams: CutoffParams, depth: number): number {
    assert(cutoffParams.uniformValues.u_cutoff_params.length === 4);
    const near = cutoffParams.uniformValues.u_cutoff_params[0];
    const far = cutoffParams.uniformValues.u_cutoff_params[1];
    const cutoffStart = cutoffParams.uniformValues.u_cutoff_params[2];
    const cutoffEnd = cutoffParams.uniformValues.u_cutoff_params[3];

    if (far === near || cutoffEnd === cutoffStart) {
        return 1.0;
    }

    const linearDepth = (depth - near) / (far - near);
    return clamp((linearDepth - cutoffStart) / (cutoffEnd - cutoffStart), 0.0, 1.0);
}

function calculateFrontCutoffOpacity(tileModelMatrix: mat4, tr: Transform, aabb: Aabb, cutoffParams: [number, number, number]) {
    // The cutoff opacity is completely disabled when pitch is lower than 20.
    const fullyOpaquePitch: number = 20.0;
    const fullyTransparentPitch: number = 40.0;
    assert(fullyOpaquePitch !== fullyTransparentPitch);
    if (tr.pitch < fullyOpaquePitch) {
        return 1.0;
    }

    const mat = tr.getWorldToCameraMatrix();
    mat4.multiply(mat, mat, tileModelMatrix);

    // The cutoff opacity is calculated based on how much below the view the AABB bottom corners are.
    // For this, we find the AABB points with the highest and lowest y value in the view space.
    const p = vec4.fromValues(aabb.min[0], aabb.min[1], aabb.min[2], 1.0);
    let r = vec4.transformMat4(vec4.create(), p, mat);
    let pMin = r;
    let pMax = r;
    p[1] = aabb.max[1];
    r = vec4.transformMat4(vec4.create(), p, mat);
    pMin = r[1] < pMin[1] ? r : pMin;
    pMax = r[1] > pMax[1] ? r : pMax;
    p[0] = aabb.max[0];
    r = vec4.transformMat4(vec4.create(), p, mat);
    pMin = r[1] < pMin[1] ? r : pMin;
    pMax = r[1] > pMax[1] ? r : pMax;
    p[1] = aabb.min[1];
    r = vec4.transformMat4(vec4.create(), p, mat);
    pMin = r[1] < pMin[1] ? r : pMin;
    pMax = r[1] > pMax[1] ? r : pMax;

    const cutoffStartParam = clamp(cutoffParams[0], 0.0, 1.0);
    // 100.0 is used here just because it provides a nice looking maximum value for the fade effect.
    // This value could be increased to allow longer fade ranges.
    const cutoffRangeParam = 100.0 * tr.pixelsPerMeter * clamp(cutoffParams[1], 0.0, 1.0);
    const finalOpacity = clamp(cutoffParams[2], 0.0, 1.0);
    const cutoffStart = vec4.lerp(vec4.create(), pMin, pMax, cutoffStartParam);

    const fovScale = Math.tan(tr.fovX * 0.5);
    // Lowest y coordinate that's still visible at the depth of the cutoff start point.
    const yMinLimit = -cutoffStart[2] * fovScale;
    if (cutoffRangeParam === 0.0) {
        return (cutoffStart[1] < -Math.abs(yMinLimit)) ? finalOpacity : 1.0;
    }

    const cutoffFactor = (-Math.abs(yMinLimit) - cutoffStart[1]) / cutoffRangeParam;
    const opacity = clamp(lerp(1.0, finalOpacity, cutoffFactor), finalOpacity, 1.0);

    return lerp(1.0, opacity, clamp((tr.pitch - fullyOpaquePitch) / (fullyTransparentPitch - fullyOpaquePitch), 0.0, 1.0));
}
